import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { ApiError, api, apiErrorCode } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { ConnectionForm } from "./ConnectionPanel";
import { ConnectHeading } from "./parts";
import { connectorKeys, lowerFirst } from "@/lib/connectors";

// The connection form alone, for someone in the org with no role in this Boo
// who opened the link Boo sent (allowed when the org lets everyone set up
// connectors). Shows the connector's name, its values, Save and test and the
// last test; nothing else of the Boo.
export function ConnectorSetupPage({ containerId, slug }: { containerId: string; slug: string }) {
  const queryClient = useQueryClient();
  const key = connectorKeys.setup(containerId, slug);
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.connectorSetup.get(containerId, slug),
  });
  const view = query.data;
  useDocumentTitle(view ? `Connect ${view.name}` : "Connect");

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-6">
        {query.isLoading ? (
          <LoadingIndicator />
        ) : query.isError ? (
          <SetupError error={query.error} />
        ) : !view ? null : (
          <>
            <ConnectHeading name={view.name} booName={view.booName} />
            {view.verify?.description && (
              <p className="text-sm text-muted-foreground text-pretty">
                The connection test {lowerFirst(view.verify.description)}
              </p>
            )}
            <ConnectionForm
              setup
              connector={{ ...view, live: view.status === "live" || view.status === "disabled" }}
              containerId={containerId}
              canEdit
              canManage={false}
              save={(values) => api.connectorSetup.saveVariables(containerId, slug, values)}
              onChanged={() => queryClient.invalidateQueries({ queryKey: key })}
            />
          </>
        )}
      </div>
    </div>
  );
}

function SetupError({ error }: { error: unknown }) {
  if (apiErrorCode(error) === "setup_not_allowed" || (error instanceof ApiError && error.status === 403)) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border p-8 text-center">
        <Lock className="size-6 text-muted-foreground" />
        <p className="text-sm font-medium">Only this Boo’s admins can set up its custom integrations</p>
        <p className="max-w-md text-sm text-muted-foreground text-pretty">
          Ask one of them to fill it in. An owner or admin of your organization can change who sets up custom
          integrations in Manage.
        </p>
      </div>
    );
  }
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border p-8 text-center">
        <p className="text-sm font-medium">This connection link doesn’t work anymore</p>
        <p className="max-w-md text-sm text-muted-foreground text-pretty">
          The custom integration may have been removed, or it belongs to another organization. Ask Boo for a new link.
        </p>
      </div>
    );
  }
  return <InlineError error={error} />;
}
